import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { apiGqAdminClient } from '../../api-client/api-gqadmin-client/axios';
import { LogoLoader } from '../../components/atoms';
import { 
    BannerSection, 
    ContentSections, 
    HeaderSection, 
    LogoSection, 
    PaymentLinkExpiry, 
    PaymentSection, 
    RewardSections, 
    SearchSection, 
    SupportSections 
} from '../../components/molecules';
import { ERP_SCRIPT_SOURCE, PLATFORM_CODE } from '../../../constants';
import { useExternalScript, useQuery } from '../../hooks';
import { generateSecureUUID, showErrorToast } from '../../utils/utils';
import { 
    postAuthPlatformAuthorizeV01,
    postAuthPlatformAuthorizeV01Selector,
} from '../../reduxtoolkit/slices/gq-admin/auth';
import { 
    getPaymentPagesFetch,
    getPaymentPagesFetchSelector,
} from '../../reduxtoolkit/slices/gq-admin/payment-pages';
import { gqSvcPaymentPageApiClient } from '../../api-client/api-svcpp-client/axios';
import { sendAnalytics } from '../../analytics';
import { mixpanelRegister } from '../../analytics/mixpanel';
import { ANALYTICS_EVENTS } from '../../utils/constants';
import { paymentPagesFeeTypesFetchGet } from '../../api-client/api-gqadmin-client';
import { localStorageUtil } from '../../utils/localStorageUtil';
import classes from './PaymentPage.module.scss';

const PaymentPage = () => {
    const [pageData, setPageData] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    const [isApiLoading, setIsApiLoading] = useState(true);
    const script_state = useExternalScript(ERP_SCRIPT_SOURCE);
    const params = useParams();
    const query = useQuery();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showDynamicFeeHeaderError, setShowDynamicFeeHeaderError] = useState(false);

    // Admin API Auth
    const fetchGqAdminAuth = async (payload) => {
        try {
            const postAuthPlatformAuthorizeV01Res = await dispatch(postAuthPlatformAuthorizeV01(payload)).unwrap();
            if (postAuthPlatformAuthorizeV01Res?.status_code === 200 && postAuthPlatformAuthorizeV01Res?.data) {
                const { access_token, refresh_token } = postAuthPlatformAuthorizeV01Res?.data;
                localStorageUtil.setItem('_gqadm_at', access_token);
                localStorageUtil.setItem('_gqadm_rt', refresh_token);
                return postAuthPlatformAuthorizeV01Res;
            }
        } catch (error) {
            console.error("fetchGqAdminAuth error", error);
            if (error?.status_code !== 401 || error?.status_code !== 403) {
                showErrorToast(error?.message || "Oops! Something went wrong");
            }
        }
    }

    //body level event listener to listen anchor tag clicks
    const handleAnchorTagClick = (event) => {
        const STATIC_DISTINCT_ID = document.documentElement.getAttribute("data-mxp-id");
        let target = event?.target?.closest("a");
        if (target && target.href) {
            if (target.target === "_blank") {
                event.preventDefault();
                let url = new URL(target.href);
                url.searchParams.set("mxp_did", STATIC_DISTINCT_ID);
                window.open(url.toString(), "_blank");
            }
        }
    }

    // Mixpanel
    const handleMixpanelEvents = () => {
        let STATIC_DISTINCT_ID = ''
        if(!query.get('mxp_did')) {
            STATIC_DISTINCT_ID = `anon-${generateSecureUUID()}`
        } else {
            STATIC_DISTINCT_ID = query.get('mxp_did');
        }
        document.documentElement.setAttribute("data-mxp-id", STATIC_DISTINCT_ID);
        const REGISTER_DATA = {
            http_referer: window.document.location.href,
            slug: params?.slug,
        }
        mixpanelRegister(REGISTER_DATA);
    }

    // fetch payment page details
    const fetchPaymentPagesDetails = async (payload) => {
        try {
            handleMixpanelEvents();
            const getPaymentPagesFetchRes = await dispatch(getPaymentPagesFetch(payload)).unwrap();
            if (getPaymentPagesFetchRes?.status_code === 200 && getPaymentPagesFetchRes?.data) {
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_FETCH_PAGE_API.SUCCESS,
                    }
                })
                const {data: paymentPageData } = getPaymentPagesFetchRes || {};
                const {institute_config: instConfig } = paymentPageData || {};
                const REGISTER_DATA = {
                    payment_page_type: paymentPageData?.payment_page_type,
                    group_id: instConfig?.group_id,
                    institute_id: instConfig?.institute_id,
                    location_id: instConfig?.location_id,
                    education_type_id: instConfig?.education_type_id,
                    ay_id: instConfig?.ay_id,
                    class_id: instConfig?.gilec_id,
                    group_name: instConfig?.group_name,
                    institute_name: instConfig?.institute_name,
                    location_name: instConfig?.location_name,
                    education_type_name: instConfig?.education_type_name,
                    ay_name: instConfig?.ay_name,
                    class_name: instConfig?.gilec_name,
                }
                mixpanelRegister(REGISTER_DATA);
                const paymentPageType = paymentPageData?.payment_page_type ?? ''; 
                const isDynamicPayPage = paymentPageData?.dynamic_link ?? false;
                // Mixpanel
                if(paymentPageType === 'STATIC-PAGE') {
                    sendAnalytics({
                        vendor: ['mixpanel', 'ga'],
                        eventData: {
                            event: ANALYTICS_EVENTS.PP_STATIC_PAGE_LOADED,
                        }
                    })
                } else if(paymentPageType === 'SEARCH-PAGE') {
                    sendAnalytics({
                        vendor: ['mixpanel', 'ga'],
                        eventData: {
                            event: ANALYTICS_EVENTS.PP_SEARCH_PAGE_LOADED
                        }
                    })
                } else if(paymentPageType === 'WORKFLOW-PAGE') {
                    sendAnalytics({
                        vendor: ['mixpanel', 'ga'],
                        eventData: {
                            event: ANALYTICS_EVENTS.PP_WORKFLOW_PAGE_LOADED
                        }
                    })
                } else if (isDynamicPayPage) {
                    sendAnalytics({
                        vendor: ['mixpanel', 'ga'],
                        eventData: {
                            event: ANALYTICS_EVENTS.PP_DYNAMIC_PAGE_LOADED,
                        }
                    });
                }
                else {
                    sendAnalytics({
                        vendor: ['mixpanel', 'ga'],
                        eventData: {
                            event: ANALYTICS_EVENTS.PP_LOADED,
                            trackingData: REGISTER_DATA,
                        }
                    });
                }
                setPageData(getPaymentPagesFetchRes?.data);

                if (getPaymentPagesFetchRes?.data?.dynamic_link) {

                    // Dynamic Page checks - only one fee type is allowed
                    const paymentPagesFeeTypesFetchGetRes = await paymentPagesFeeTypesFetchGet({
                        ...payload,
                        dynamic_link: true,
                        is_unique: true,
                    });

                    if (paymentPagesFeeTypesFetchGetRes?.data?.data?.length > 0) {
                        const allFeeHeaderData = paymentPagesFeeTypesFetchGetRes?.data?.data;
                        const dynamicFeeTypes = [];
                        allFeeHeaderData.forEach(fee_header => {
                            if (fee_header?.dynamic_ft) {
                                dynamicFeeTypes?.push(fee_header)
                            }
                        });
                        if (dynamicFeeTypes?.length === 0) {
                            setShowDynamicFeeHeaderError(true);
                            sendAnalytics({
                                vendor: ['mixpanel', 'ga'],
                                eventData: {
                                    event: ANALYTICS_EVENTS.PP_DYNAMIC_PAGE_DYN_FT_NOT_AVAILABLE,
                                }
                            })
                        }
                    } else {
                        setShowDynamicFeeHeaderError(true);
                        sendAnalytics({
                            vendor: ['mixpanel', 'ga'],
                            eventData: {
                                event: ANALYTICS_EVENTS.PP_DYNAMIC_PAGE_DYN_FT_NOT_AVAILABLE,
                            }
                        })
                    }
                }
            } else {
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_FETCH_PAGE_API.FAILED,
                    }
                })
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_LOADED,
                    }
                });

                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_REDIRECT_HELP_PAGE
                    }
                })
                navigate(`/help`);
            }
        } catch (error) {
            console.error("fetchPaymentPagesDetails error", error);
            if (error?.status_code !== 401 || error?.status_code !== 403) {
                showErrorToast(error?.message || "Oops! Something went wrong");
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_FETCH_PAGE_API.FAILED,
                        trackingData: {
                            err_message: error?.message,
                            err_code: error?.status_code,
                        }
                    }
                })
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_LOADED,
                    }
                });
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_REDIRECT_HELP_PAGE
                    }
                })
                navigate(`/help`);
            }
        }
    }

    // Fetch session data by code
    const fetchSessionData = async (code) => {
        return await gqSvcPaymentPageApiClient({
            method: 'GET',
            url: `/v1/misc/session/fetch?session_code=${code}`,
        })
    }

    const fetchInitalPageData = async () => {
        try {
            setIsApiLoading(true);

            let session_api_response;
            if (query.get('scode')) {
                const fetchSessionDataRes = await fetchSessionData(query.get('scode'));
                if (fetchSessionDataRes?.data?.status_code === 200) {
                    mixpanelRegister({
                        customer_session_code: query.get('scode'),
                    });
                    session_api_response = fetchSessionDataRes?.data?.data?.session_data;
                    setSessionData(session_api_response);
                }
            }

            // Update Session Data
            const updateSessionData = async (payload) => {
                try {
                    const res = await gqSvcPaymentPageApiClient({
                        method: 'PATCH',
                        url: `/v1/misc/session/update`,
                        data: payload,
                    });
                    return res?.data;
                } catch (error) {
                    console.error('updateSessionData error', error);
                    return null;
                }
            };

            let PPSC_FLOW = false;
            if (query.get('ppsc')) {
                const ppsc_data = await fetchSessionData(query.get('ppsc'));
                if (ppsc_data?.data?.status_code === 200 && ppsc_data?.data?.data?.status === 'ACTIVE') {
                    if (ppsc_data?.data?.data?.session_data?.original_fee_types) {
                        const updateSessionPayload = {
                            // ...ppsc_data?.data?.data,
                            session_data: {
                                ...ppsc_data?.data?.data?.session_data,
                                fee_types: ppsc_data?.data?.data?.session_data?.original_fee_types,
                            },
                            session_code: query.get('ppsc'),
                        };
                        await updateSessionData(updateSessionPayload);
                    }
                    PPSC_FLOW = true;
                    mixpanelRegister({
                        customer_session_code: query.get('ppsc'),
                    });
                }
            }

            await fetchPaymentPagesDetails({
                payment_page_slug: params?.slug,
                ...(query.get('sbdc') && { sbd_code: query.get('sbdc') }),
                ...(session_api_response?.sbd_code && { sbd_code: session_api_response?.sbd_code }),
                ...(query.get('ppsc') && PPSC_FLOW && { session_code: query.get('ppsc') }),
                ...(query.get('uid') && { pp_redirect_uid: query.get('uid') }),
            });
        } catch (error) {
            console.error("fetchInitalPageData error", error);
        } finally {
             setIsApiLoading(false);
        }
    };

    useEffect(() => {
        fetchInitalPageData();
        document.body.addEventListener('click', handleAnchorTagClick);

        return () => {
            document.body.removeEventListener('click', handleAnchorTagClick);
        }
    }, [])


    if (isApiLoading || script_state === 'loading') {
        return (<div className={classes.FullPageLoader}>
            <LogoLoader />
        </div>)
    }
    
    return (
        <>
            {pageData?.logo_block?.visible ? 
                (<section className={classes.PageSection}>
                    <div className="row">
                        <div className="col-lg-12">
                            <LogoSection data={pageData} />
                        </div>
                    </div>
                </section>)
            : null}

            {pageData?.header_block?.visible ? 
                (<section className={classes.PageSection}>
                    <div className="row">
                        <div className="col-lg-12">
                            <HeaderSection data={pageData} />
                        </div>
                    </div>
                </section>)
            : null}

            {(pageData?.payment_page_expired) ? 
                (<section className={classes.PageSection}>
                    <div className="row">
                        <div className="col-lg-9 col-md-10 col-sm-12 mx-auto">
                            <PaymentLinkExpiry />
                        </div>
                    </div>
                </section>)
            : (pageData?.search_block?.visible || pageData?.sdk_block?.length > 0) && 
                <section className={classes.PageSection}>
                    <div className="row">
                        {!showDynamicFeeHeaderError ? 
                            (<div className="col-lg-9 col-md-10 col-sm-12 mx-auto">
                                {!pageData?.search_block?.visible ? 
                                    (<PaymentSection data={pageData} sessionData={sessionData} />)
                                : (<SearchSection data={pageData} />)}
                            </div>)
                        : (<div className="col-lg-9 col-md-10 col-sm-12 mx-auto">
                            <div className={classes.FeeTypeIssueBlock}>
                                <div className={classes.Icon}>
                                    <svg  xmlns="http://www.w3.org/2000/svg"  width={48}  height={48}  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth={2}  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 9v4" /><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" /><path d="M12 16h.01" /></svg>
                                </div>
                                <h3 className={classes.Title}>Oops! Something went wrong</h3>
                                <p className={classes.Text}>
                                    Invalid Payment Configurations.
                                </p>
                            </div>
                        </div>)}
                    </div>
                </section>}

            {pageData?.banner_block?.visible ? 
                (<section className={classes.PageSection}>
                    <div className="row">
                        <div className="col-lg-12">
                            <BannerSection data={pageData} />
                        </div>
                    </div>
                </section>)
            : null}

            {pageData?.content_blocks?.length > 0 ? 
                (<section className={classes.PageSection}>
                    {/* More than one content sections */}
                    <ContentSections data={pageData} />
                </section>)
            : null}

            {pageData?.reward_block?.visible ? 
                (<section className={classes.PageSection}>
                    <div className="row">
                        <div className="col-lg-12">
                            <RewardSections data={pageData} />
                        </div>
                    </div>
                </section>)
            : null}

            {pageData?.support_block?.visible ? 
                (<section className={classes.PageSection}>
                    <div className="row">
                        <div className="col-lg-12">
                            <SupportSections data={pageData} />
                        </div>
                    </div>
                </section>)
            : null}
        </>
    )
}

export default PaymentPage;